import React, { useState, useEffect, useMemo } from 'react';
import { View, ScrollView } from 'react-native';
import { interpolateBlues, schemeCategory10 } from 'd3-scale-chromatic';
import LineChartScreen from '../common/charts/lineChart-builder';
import PaperTableDynamic from '../common/controls/TableProvider';
import PieChartComp from '../common/charts/piChart-builder';
import { findImmediateParent, getAllItems, getItemById } from '../common/service/dataOperations';
import { useAuth } from '../../../providers/AuthProvider';
import { useClient } from '../../../providers/ClientProvider';
import { scaleOrdinal } from 'd3-scale';
import CustomDT from '../common/controls/customDataTable';
import { Loader } from '../../Common/Loader';
import { colorType3, colorType5, logStyles } from './LogStyles';
import { TabScreen, Tabs, TabsProvider } from 'react-native-paper-tabs';
import { useNav } from '../../../providers/NavProvider';
import LogKpiRow from './LogKpiRow';
import LogDateRangeFilter from '../common/controls/LogDateRangeFilter';

interface Props {
  siteId: string;
  logTitle: string;
}

export default function WasteLog(props: Props) {
  const [wasteLogData, setWasteLogData] = useState<null | any>(null);
  const [wasteLogColumns, setWasteLogColumns] = useState<null | any>(null);
  const { getAccessToken } = useAuth();
  const { clientAPIURL } = useClient();
  const { sitesResp } = useNav();

  useEffect(() => {
    async function fetchData() {
      try {
        const packageSite = sitesResp && findImmediateParent(sitesResp, props.siteId);
        if (packageSite && packageSite.id) {
          const accessToken = await getAccessToken();
          const json = await getAllItems(props.logTitle, accessToken, clientAPIURL, props.siteId, undefined, packageSite.id);
          setWasteLogData(json.items);
          setWasteLogColumns(json.columns);
        }
      } catch (error) {
        console.error(error);
      }
    }
    sitesResp && fetchData();
  }, [sitesResp]);

  return wasteLogData && wasteLogColumns
    ? <CreateLogCharts
        setWasteLogData={setWasteLogData}
        wasteLogData={wasteLogData}
        wasteLogColumns={wasteLogColumns}
        siteId={props.siteId}
        logTitle={props.logTitle}
        clientAPIURL={clientAPIURL}
      />
    : <Loader />;
}

interface CreateLogChartsProps {
  wasteLogData: any;
  wasteLogColumns: any;
  siteId: string;
  logTitle: string;
  clientAPIURL: string;
  setWasteLogData: any;
}

const CreateLogCharts: React.FC<CreateLogChartsProps> = ({
  setWasteLogData, wasteLogData, wasteLogColumns, siteId, logTitle, clientAPIURL,
}) => {
  const { currentLeafObj } = useNav();
  const defaultTab = currentLeafObj.permissions &&
    JSON.stringify(currentLeafObj.permissions).toLowerCase().includes('write')
    ? 'list' : 'chart';
  const { getAccessToken } = useAuth();

  const ColumnsWithIDColumn = wasteLogColumns && [
    ...wasteLogColumns,
    {
      id: 'CODE_GENERATED_ID',
      title: 'Item ID',
      columnName: 'ID',
      list: '7ae860e9-6c67-46d0-b65e-f2869542a3aa',
      datatype: 'calculated',
      createdAt: 'YOUR_CREATED_AT_TIMESTAMP',
      modifiedAt: 'YOUR_MODIFIED_AT_TIMESTAMP',
      required: false,
      subType: 'Float',
      choiceChoices: null,
      choiceType: null,
      choiceMultiselect: null,
      multiline: false,
      order: 0,
      calculatedFormula: null,
      listNavigation: null,
    },
  ];

  const [showFilter, setShowFilter] = useState<boolean>(true);
  const [filteredData, setFilteredData] = useState<any[]>(wasteLogData ?? []);

  async function handleDataUpdate(item: any) {
    if (!item.ID) return;
    try {
      const accessToken = await getAccessToken();
      const result = await getItemById(logTitle, accessToken, clientAPIURL, siteId, item.ID);
      const rawItem = result?.items?.[0];
      if (!rawItem) return;
      // getItemById returns raw API data — apply UTC+4 date formatting to match processData output
      const formatDate = (v: any): string => {
        if (!v) return v;
        const adj = new Date(new Date(v).getTime() + 4 * 60 * 60000);
        const d = adj.getDate(), m = adj.getMonth() + 1, y = adj.getFullYear();
        return `${d < 10 ? '0' + d : d}/${m < 10 ? '0' + m : m}/${y}`;
      };
      const updatedItem = { ...rawItem, Date: formatDate(rawItem.Date) };
      setFilteredData(prev => [...prev.filter((o: any) => o.ID !== item.ID), updatedItem]);
    } catch (ex) {
      console.error(ex);
    }
  }

  const chartData = useMemo(() => {
    // Quantity-filtered subset — charts only include records with a recorded quantity
    const quantityData = filteredData.filter(
      (item: any) => item.QuantityTonne !== null && item.QuantityTonne !== '',
    );
    const uniquePeriod = [...new Set(quantityData.map((item: any) => item.Period))].filter(Boolean) as string[];
    const uniqueDisposalMethods = [...new Set(quantityData.map((item: any) => item.WasteDisposalMethod))].filter(Boolean) as string[];
    const uniqueWasteCategories = [...new Set(quantityData.map((item: any) => item.WasteCategory))].filter(Boolean) as string[];

    const colorScaleLine = scaleOrdinal(schemeCategory10);

    // LineChartScreen contract: labels=string[], datasets[].data=number[], datasets[].color=(opacity)=>string
    const lineChartDataByMethod: Record<string, number[]> = {};
    uniqueDisposalMethods.forEach(m => { lineChartDataByMethod[m] = []; });
    uniquePeriod.forEach(period => {
      uniqueDisposalMethods.forEach(method => {
        const sum = quantityData
          .filter((item: any) => item.Period === period && item.WasteDisposalMethod === method)
          .reduce((acc: number, item: any) => acc + (Number(item.QuantityTonne) || 0), 0);
        lineChartDataByMethod[method].push(sum);
      });
    });
    const datasetsMethod = Object.entries(lineChartDataByMethod).map(([, values], idx) => ({
      data: values,
      strokeWidth: 2,
      color: (opacity = 1) => colorScaleLine(idx.toString()),
    }));
    const lineDataByMethod = { labels: uniquePeriod, datasets: datasetsMethod };
    const legendByMethod = uniqueDisposalMethods.map(name => ({ name }));

    const lineChartDataByType: Record<string, number[]> = {};
    uniqueWasteCategories.forEach(cat => { lineChartDataByType[cat] = []; });
    uniquePeriod.forEach(period => {
      uniqueWasteCategories.forEach(cat => {
        const sum = quantityData
          .filter((item: any) => item.Period === period && item.WasteCategory === cat)
          .reduce((acc: number, item: any) => acc + (Number(item.QuantityTonne) || 0), 0);
        lineChartDataByType[cat].push(sum);
      });
    });
    const datasetsType = Object.entries(lineChartDataByType).map(([, values], idx) => ({
      data: values,
      strokeWidth: 2,
      color: (opacity = 1) => colorScaleLine(idx.toString()),
    }));
    const lineDataByType = { labels: uniquePeriod, datasets: datasetsType };
    const legendByType = uniqueWasteCategories.map(name => ({ name }));

    // PieChartComp contract: data[].name=string(non-null), data[].value=number, data[].color=string
    // These pies sum QuantityTonne (not counts) — cannot use buildPieData from chartDataUtils
    const sumByKey = (arr: any[], key: string): Record<string, number> => {
      const acc: Record<string, number> = {};
      arr.forEach((item: any) => {
        const k = item[key];
        if (k != null && String(k) !== 'null' && String(k) !== 'undefined') {
          acc[String(k)] = (acc[String(k)] ?? 0) + (Number(item.QuantityTonne) || 0);
        }
      });
      return acc;
    };
    const toPieData = (totals: Record<string, number>, colorFn: (k: string) => string) =>
      Object.entries(totals).map(([name, value], idx) => ({
        name,
        value: Math.round(value),
        color: colorFn(idx.toString()),
      }));

    const byWasteCategoryPieData = toPieData(sumByKey(filteredData, 'WasteCategory'), k => colorType5(k));
    const byWasteDisposalMethodPieData = toPieData(sumByKey(filteredData, 'WasteDisposalMethod'), k => colorType3(k));
    const byDivertedPieData = toPieData(sumByKey(filteredData, 'Diverted'), k => colorType5(k));
    const byTypeNonHazardousPieData = toPieData(
      sumByKey(filteredData.filter((i: any) => i.Hazardous === 'Non Hazardous'), 'WasteType'), k => colorType5(k),
    );
    const byTypeHazardousPieData = toPieData(
      sumByKey(filteredData.filter((i: any) => i.Hazardous === 'Hazardous'), 'WasteType'), k => colorType3(k),
    );

    // KPI summary
    const totalQty = Math.round(filteredData.reduce((s: number, i: any) => s + (Number(i.QuantityTonne) || 0), 0));
    // WasteCategory === 'Hazardous' reliably identifies hazardous items
    // Non Haz derived as Total − Hazardous so the three cards always sum correctly
    const hazQty = parseFloat(
      filteredData.filter((i: any) => i.WasteCategory === 'Hazardous')
        .reduce((s: number, i: any) => s + (Number(i.QuantityTonne) || 0), 0).toFixed(2),
    );
    const nonHazQty = parseFloat((totalQty - hazQty).toFixed(2));
    const divertedQty = Math.round(
      filteredData.filter((i: any) => i.Diverted === 'Diverted')
        .reduce((s: number, i: any) => s + (Number(i.QuantityTonne) || 0), 0),
    );
    const notDivertedQty = Math.round(
      filteredData.filter((i: any) => i.Diverted === 'Not Diverted')
        .reduce((s: number, i: any) => s + (Number(i.QuantityTonne) || 0), 0),
    );
    const kpiItems = [
      { label: 'Total Qty (T)',     value: totalQty,        color: '#00695C' },
      { label: 'Entries',           value: filteredData.length, color: '#4527A0' },
      { label: 'Hazardous (T)',     value: hazQty,           color: '#C62828' },
      { label: 'Non Haz (T)',       value: nonHazQty,        color: '#2E7D32' },
      { label: 'Diverted (T)',      value: divertedQty,      color: '#1565C0' },
      { label: 'Not Diverted (T)',  value: notDivertedQty,   color: '#F57C00' },
    ];

    // Table 1: Period × WasteDisposalMethod with totals
    // Fix: original used wrong field name 'DisposalMethod' — correct field is 'WasteDisposalMethod'
    const disposalMethodChoices = (wasteLogColumns.find((col: any) => col.columnName === 'WasteDisposalMethod')?.choiceChoices ?? '')
      .split('#;#').filter((v: string) => v && v !== 'undefined');

    const table1Map: Record<string, any> = {};
    filteredData.forEach((item: any) => {
      const { Period, WasteDisposalMethod, QuantityTonne, WasteCategory, Diverted } = item;
      if (!Period) return;
      if (!table1Map[Period]) {
        table1Map[Period] = { Period };
        disposalMethodChoices.forEach((m: string) => { table1Map[Period][m] = 0; });
        Object.assign(table1Map[Period], {
          'Number Of Samples': 0, Total: 0, Hazardous: 0,
          'Non Hazardous': 0, 'Total Not Diverted (Tonnes)': 0, 'Total Diverted (Tonnes)': 0,
        });
      }
      const qty = Number(QuantityTonne) || 0;
      if (WasteDisposalMethod && table1Map[Period][WasteDisposalMethod] !== undefined) {
        table1Map[Period][WasteDisposalMethod] += Math.round(qty);
      }
      table1Map[Period]['Number Of Samples']++;
      table1Map[Period]['Total'] += qty;
      if (WasteCategory === 'Hazardous') table1Map[Period]['Hazardous'] += qty;
      if (WasteCategory === 'Non Hazardous') table1Map[Period]['Non Hazardous'] += qty;
      if (Diverted === 'Diverted') table1Map[Period]['Total Diverted (Tonnes)'] += qty;
      if (Diverted === 'Not Diverted') table1Map[Period]['Total Not Diverted (Tonnes)'] += qty;
    });
    const mergedData1 = Object.values(table1Map).map((row: any) => {
      const div = row['Total Diverted (Tonnes)'];
      const notDiv = row['Total Not Diverted (Tonnes)'];
      const total = div + notDiv;
      const pct = total > 0 ? ((div / total) * 100).toFixed(0) : '0';
      const byDisposalTotal = disposalMethodChoices.reduce((s: number, m: string) => s + (row[m] || 0), 0);
      return { ...row, Percentage: pct, 'Total (By Disposal Method)': byDisposalTotal };
    });
    const columnKeys1 = mergedData1.length > 0
      ? Object.keys(mergedData1[0]).map((key, idx) => ({ ColumnName: key, Order: idx }))
      : [];

    // Table 2: Non-Hazardous waste quantities by WasteType per period
    const nonHazTypes = [...new Set(
      filteredData.filter((i: any) => i.Hazardous === 'Non Hazardous').map((i: any) => i.WasteType).filter(Boolean),
    )] as string[];
    const table2Map: Record<string, any> = {};
    filteredData.filter((i: any) => i.Hazardous === 'Non Hazardous').forEach((item: any) => {
      const { Period, WasteType, QuantityTonne } = item;
      if (!Period) return;
      if (!table2Map[Period]) {
        table2Map[Period] = { Period };
        nonHazTypes.forEach((t: string) => { table2Map[Period][t] = 0; });
      }
      if (WasteType) table2Map[Period][WasteType] = (table2Map[Period][WasteType] ?? 0) + Math.round(Number(QuantityTonne) || 0);
    });
    const mergedData = Object.values(table2Map).map((row: any) => ({
      ...row,
      'Total (By Waste Type)': nonHazTypes.reduce((s: number, t: string) => s + (row[t] || 0), 0),
    }));
    const columnKeys = mergedData.length > 0
      ? Object.keys(mergedData[0]).map((key, idx) => ({ ColumnName: key, Order: idx }))
      : [];

    // Table 3: Hazardous waste quantities by WasteType per period
    const hazTypes = [...new Set(
      filteredData.filter((i: any) => i.Hazardous === 'Hazardous').map((i: any) => i.WasteType).filter(Boolean),
    )] as string[];
    const table3Map: Record<string, any> = {};
    filteredData.filter((i: any) => i.Hazardous === 'Hazardous').forEach((item: any) => {
      const { Period, WasteType, QuantityTonne } = item;
      if (!Period) return;
      if (!table3Map[Period]) {
        table3Map[Period] = { Period };
        hazTypes.forEach((t: string) => { table3Map[Period][t] = 0; });
      }
      if (WasteType) table3Map[Period][WasteType] = (table3Map[Period][WasteType] ?? 0) + (Math.round(Number(QuantityTonne)) || 0);
    });
    const mergedData2 = Object.values(table3Map).map((row: any) => ({
      ...row,
      'Total (By Waste Type)': hazTypes.reduce((s: number, t: string) => s + (row[t] || 0), 0),
    }));
    const columnKeys2 = mergedData2.length > 0
      ? Object.keys(mergedData2[0]).map((key, idx) => ({ ColumnName: key, Order: idx }))
      : [];

    return {
      kpiItems,
      lineDataByMethod, legendByMethod,
      lineDataByType, legendByType,
      byWasteCategoryPieData, byWasteDisposalMethodPieData,
      byDivertedPieData, byTypeNonHazardousPieData, byTypeHazardousPieData,
      mergedData1, columnKeys1,
      mergedData, columnKeys,
      mergedData2, columnKeys2,
    };
  }, [filteredData, wasteLogColumns]);

  const {
    kpiItems,
    lineDataByMethod, legendByMethod,
    lineDataByType, legendByType,
    byWasteCategoryPieData, byWasteDisposalMethodPieData,
    byDivertedPieData, byTypeNonHazardousPieData, byTypeHazardousPieData,
    mergedData1, columnKeys1,
    mergedData, columnKeys,
    mergedData2, columnKeys2,
  } = chartData;

  return (
    <View style={logStyles.logRoot}>
      <TabsProvider defaultIndex={defaultTab === 'list' ? 2 : 0}>
        <Tabs style={{ backgroundColor: '#fff', height: '100%' }} showLeadingSpace={true}>

          <TabScreen label="Charts" icon="chart-bar">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              <View style={{ zIndex: 200 }}>
                <LogKpiRow items={kpiItems}>
                  <LogDateRangeFilter data={wasteLogData} onFilterChange={setFilteredData} />
                </LogKpiRow>
              </View>
              <View>
                <View style={logStyles.container}>
                  <View style={[logStyles.card, { backgroundColor: '' }]}>
                    <PieChartComp data={byWasteCategoryPieData} colorPallate={interpolateBlues} subTitle={'(Cumulative - Tonne)'} ChartTitle={'Waste Qty / Category'} />
                  </View>
                  <View style={[logStyles.card, { backgroundColor: '' }]}>
                    <LineChartScreen legend={legendByType} data={lineDataByType} label={lineDataByType.labels} subTitle={'(Monthly - Tonne)'} ChartTitle={'Waste Qty / Category'} yAxisLabel="Tonnes" />
                  </View>
                  <View style={[logStyles.card, { backgroundColor: '' }]}>
                    <PieChartComp data={byWasteDisposalMethodPieData} colorPallate={interpolateBlues} subTitle={'(Cumulative - Tonne)'} ChartTitle={'Waste Qty / (Destination)'} />
                  </View>
                  <View style={[logStyles.card, { backgroundColor: '' }]}>
                    <LineChartScreen legend={legendByMethod} data={lineDataByMethod} label={lineDataByMethod.labels} subTitle={'(Monthly - Tonne)'} ChartTitle={'Waste Qty / Destination'} yAxisLabel="Tonnes" />
                  </View>
                  <View style={[logStyles.card, { backgroundColor: '' }]}>
                    <PieChartComp data={byTypeNonHazardousPieData} colorPallate={interpolateBlues} subTitle={'(Cumulative - Tonne, %)'} ChartTitle={'Non Hazardous Waste Qty / Type'} />
                  </View>
                  <View style={[logStyles.card, { backgroundColor: '' }]}>
                    <PieChartComp data={byTypeHazardousPieData} colorPallate={interpolateBlues} subTitle={'(Cumulative - Tonne, %)'} ChartTitle={'Hazardous Waste Qty / Type'} />
                  </View>
                  <View style={[logStyles.card, { backgroundColor: '' }]}>
                    <PieChartComp data={byDivertedPieData} colorPallate={interpolateBlues} subTitle={'(Cumulative - Tonne, %)'} ChartTitle={'Waste Diversion'} />
                  </View>
                </View>
              </View>
            </ScrollView>
          </TabScreen>

          <TabScreen label="Tables" icon="table">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              <View>
                <View style={logStyles.container}>
                  <View style={logStyles.cardThree}>
                    {mergedData1 && columnKeys1 ? (
                      <CustomDT
                        data={mergedData1}
                        columns={columnKeys1}
                        siteId={siteId}
                        logTitle={logTitle}
                        tableId={'QuantitiesDiversion'}
                        tableTitle={'Waste Quantities (Tonne) & Waste Diversion'}
                      />
                    ) : null}
                  </View>
                  <View style={logStyles.cardThree}>
                    {mergedData && columnKeys ? (
                      <CustomDT
                        data={mergedData}
                        columns={columnKeys}
                        siteId={siteId}
                        logTitle={logTitle}
                        tableId={'NonHazWasteQuantities'}
                        tableTitle={'Non-Hazardous Waste Quantities (Tonne)'}
                      />
                    ) : null}
                  </View>
                  <View style={logStyles.cardThree}>
                    {mergedData2 && columnKeys2 ? (
                      <CustomDT
                        data={mergedData2}
                        columns={columnKeys2}
                        siteId={siteId}
                        logTitle={logTitle}
                        tableId={'HazWasteQuantities'}
                        tableTitle={'Hazardous Waste Quantities (Tonne)'}
                      />
                    ) : null}
                  </View>
                </View>
              </View>
            </ScrollView>
          </TabScreen>

          <TabScreen label="List" icon="database">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              <View>
                <View style={logStyles.container}>
                  <View style={logStyles.cardThree}>
                    {ColumnsWithIDColumn && (
                      <PaperTableDynamic
                        data={filteredData}
                        columns={ColumnsWithIDColumn}
                        siteId={siteId}
                        logTitle={logTitle}
                        updateData={handleDataUpdate}
                        isConsolidatedTable={false}
                        funcSetShowFilter={setShowFilter}
                      />
                    )}
                  </View>
                </View>
              </View>
            </ScrollView>
          </TabScreen>

        </Tabs>
      </TabsProvider>
    </View>
  );
};
